import { LanguageSwitcher } from '@ecom/ui-kit';
import { motion } from 'framer-motion';
import { type ReactElement, type ReactNode } from 'react';

import { useLocale } from '@/lib/locale';

/**
 * The frame of every signed-out screen. It carries the language switcher: a person who has just arrived from an email
 * (password reset) is signed out and has no other way to choose Arabic, French or English.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  icon,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
  /** An optional status illustration shown above the title. */
  icon?: ReactNode;
}): ReactElement {
  const { locale, setLocale } = useLocale();

  return (
    <div className="flex min-h-dvh flex-col items-center bg-ground px-4 py-6">
      <div className="flex w-full max-w-md justify-end">
        <LanguageSwitcher locale={locale} onChange={setLocale} />
      </div>

      <div className="flex w-full flex-1 items-center justify-center py-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          <div className="mb-6 flex items-center justify-center gap-2">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent font-display text-xl font-bold text-white"
              aria-hidden="true"
            >
              S
            </span>
            <span className="font-display text-xl font-bold text-ink">Souk</span>
          </div>

          <div className="rounded-2xl border border-line bg-surface p-7 shadow-card">
            {icon !== undefined ? <div className="mb-4 flex justify-center">{icon}</div> : null}
            <h1
              className={
                icon !== undefined
                  ? 'text-center font-display text-xl font-bold text-ink'
                  : 'font-display text-xl font-bold text-ink'
              }
            >
              {title}
            </h1>
            <p
              className={
                icon !== undefined
                  ? 'mt-1 text-center text-sm text-inkFaint'
                  : 'mt-1 text-sm text-inkFaint'
              }
            >
              {subtitle}
            </p>
            <div className="mt-6">{children}</div>
          </div>

          <div className="mt-5 text-center text-sm text-inkFaint">{footer}</div>
        </motion.div>
      </div>
    </div>
  );
}
